"use client";

import { useCallback, useRef, useState } from "react";

import type { PhraseGroup } from "@/components/ui/organisms/section-navigator/phrase-groups";
import { isGroupLooped } from "@/components/ui/organisms/section-navigator/phrase-groups";

export interface LoopRange {
  startMeasure: number;
  endMeasure: number;
}

interface MeasureGridProps {
  /**
   * Les mesures de la section, regroupées par phrase. Chaque groupe est une
   * ligne, précédée d'une poignée qui sélectionne la phrase entière.
   */
  groups: PhraseGroup[];
  currentMeasure: number;
  loop: LoopRange | null;
  dragMin: number;
  dragMax: number;
  isDragSelecting: boolean;
  onMeasurePointerDown: (m: number) => void;
  onMeasurePointerEnter: (m: number) => void;
  /** Relâchement normal du pointeur : la sélection en cours est validée. */
  onSelectionEnd: () => void;
  /** Geste interrompu par le système : la sélection en cours est abandonnée. */
  onSelectionCancel: () => void;
  onToggleGroupLoop: (group: PhraseGroup) => void;
}

/**
 * Attribut qui porte le numéro de mesure sur chaque cellule.
 *
 * Pendant un glissé, la mesure visée se résout par la position et non par le
 * survol : la capture du pointeur adresse tous les événements du geste au
 * conteneur de la grille, donc les cellules traversées ne reçoivent plus rien.
 * C'était de toute façon la seule façon de couvrir le doigt, qui ne produit
 * jamais d'événement de survol.
 */
const MEASURE_ATTRIBUTE = "data-measure";

/** Mesure située sous un point de l'écran, `null` en dehors d'une cellule. */
function measureAtPoint(clientX: number, clientY: number): number | null {
  const cell = document
    .elementFromPoint(clientX, clientY)
    ?.closest(`[${MEASURE_ATTRIBUTE}]`);

  if (!cell) {
    return null;
  }

  const measure = Number(cell.getAttribute(MEASURE_ATTRIBUTE));

  return Number.isInteger(measure) ? measure : null;
}

export function MeasureGrid({
  groups,
  currentMeasure,
  loop,
  dragMin,
  dragMax,
  isDragSelecting,
  onMeasurePointerDown,
  onMeasurePointerEnter,
  onSelectionEnd,
  onSelectionCancel,
  onToggleGroupLoop,
}: MeasureGridProps) {
  /**
   * Index du groupe survolé. Survoler la poignée éclaire les cellules du
   * groupe : on voit ce qu'on va sélectionner avant de cliquer.
   */
  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  /**
   * Mesure signalée en dernier au parent, `null` hors geste.
   *
   * Double emploi assumé : c'est à la fois le drapeau « un geste est en
   * cours » — sinon chaque survol à la souris déclencherait un test de
   * position — et le filtre qui ramène le flot de `pointermove`, un par
   * frame, à un signalement par franchissement de cellule.
   */
  const pointedMeasureRef = useRef<number | null>(null);

  const handleCellPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, measure: number) => {
      // Sans capture, le glissé meurt sur place : les événements suivants
      // restent adressés à la cellule d'appui. C'est aussi elle qui garantit
      // que le relâchement revient ici même hors du panneau, à la souris
      // comme au doigt.
      gridRef.current?.setPointerCapture(event.pointerId);
      pointedMeasureRef.current = measure;
      onMeasurePointerDown(measure);
    },
    [onMeasurePointerDown],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointedMeasureRef.current === null) {
        return;
      }

      const measure = measureAtPoint(event.clientX, event.clientY);

      if (measure === null || measure === pointedMeasureRef.current) {
        return;
      }

      pointedMeasureRef.current = measure;
      onMeasurePointerEnter(measure);
    },
    [onMeasurePointerEnter],
  );

  const handlePointerUp = useCallback(() => {
    if (pointedMeasureRef.current === null) {
      return;
    }

    pointedMeasureRef.current = null;
    onSelectionEnd();
  }, [onSelectionEnd]);

  const handlePointerCancel = useCallback(() => {
    if (pointedMeasureRef.current === null) {
      return;
    }

    pointedMeasureRef.current = null;
    onSelectionCancel();
  }, [onSelectionCancel]);

  const isMeasureInLoop = (m: number) =>
    loop !== null && m >= loop.startMeasure && m <= loop.endMeasure;

  const isMeasureInDrag = (m: number) =>
    isDragSelecting && m >= dragMin && m <= dragMax;

  return (
    <div
      ref={gridRef}
      className="px-2 pb-2 pt-0.5 ml-1 space-y-[3px]"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {groups.map((group, gIdx) => {
        const measures: number[] = [];
        for (let m = group.startMeasure; m <= group.endMeasure; m++) {
          measures.push(m);
        }

        const looped = isGroupLooped(group, loop);
        const hovered = hoveredGroup === gIdx;

        let handleColor = "rgba(255, 255, 255, 0.10)";

        if (hovered) {
          handleColor = "rgba(255, 255, 255, 0.38)";
        }

        if (looped) {
          handleColor = "var(--color-accent)";
        }

        // La poignée désigne la phrase, elle ne l'arme pas : le libellé dit
        // « sélectionner », sinon il promet un bouclage que seul le bouton
        // « Boucler » déclenche.
        const label =
          group.startMeasure === group.endMeasure
            ? `Sélectionner la mesure ${group.startMeasure}`
            : `Sélectionner les mesures ${group.startMeasure} à ${group.endMeasure}`;

        return (
          <div key={group.startMeasure} className="flex items-stretch gap-1.5">
            <button
              type="button"
              className="shrink-0 w-2.5 flex justify-center cursor-pointer rounded-sm"
              title={looped ? "Retirer la sélection" : label}
              aria-label={looped ? "Retirer la sélection" : label}
              aria-pressed={looped}
              onMouseEnter={() => setHoveredGroup(gIdx)}
              onMouseLeave={() => setHoveredGroup(null)}
              onClick={() => onToggleGroupLoop(group)}
            >
              <span
                className="w-[2px] h-full rounded-full transition-colors duration-100"
                style={{ backgroundColor: handleColor }}
              />
            </button>

            <div className="inline-grid grid-cols-4 gap-[3px]">
              {measures.map((m) => {
                const isCurrentMeasure = m === currentMeasure;
                const inLoop = isMeasureInLoop(m);
                const inDrag = isMeasureInDrag(m);
                const selected = inDrag || inLoop;

                const selStart = inDrag ? dragMin : loop?.startMeasure ?? -1;
                const selEnd = inDrag ? dragMax : loop?.endMeasure ?? -1;
                const isFirst = selected && m === selStart;
                const isLast = selected && m === selEnd;

                let bg = "rgba(36, 36, 48, 0.6)";
                let borderColor = "rgba(255, 255, 255, 0.06)";
                let textColor = "#9090a8";

                if (hovered) {
                  borderColor = "rgba(255, 255, 255, 0.16)";
                  textColor = "#c8c8d8";
                }

                if (selected) {
                  bg = "rgba(139, 92, 246, 0.15)";
                  borderColor = "rgba(139, 92, 246, 0.3)";
                  textColor = "#f0f0f5";
                }

                if (isCurrentMeasure) {
                  bg = selected
                    ? "rgba(139, 92, 246, 0.25)"
                    : "var(--color-violet-soft)";
                  borderColor = "var(--color-accent)";
                  textColor = "#f0f0f5";
                }

                return (
                  <div
                    key={m}
                    // `overflow-hidden` taille les repères de bord de boucle
                    // sur le rayon de la cellule : sans lui, leurs angles
                    // droits dépassent là où le coin s'incurve.
                    //
                    // `touch-none` sur la cellule seulement, jamais sur la
                    // grille : le panneau qui la contient défile
                    // verticalement, et le doigt doit continuer de le faire
                    // glisser depuis les gouttières, la poignée de phrase et
                    // la marge à droite des colonnes. Sur la cellule en
                    // revanche, le navigateur prendrait le cerné pour un
                    // début de défilement et volerait le geste.
                    className="relative w-8 h-8 flex flex-col items-center justify-center rounded-md overflow-hidden cursor-pointer touch-none transition-all duration-75"
                    style={{
                      backgroundColor: bg,
                      outline: `1.5px solid ${borderColor}`,
                      outlineOffset: "-1.5px",
                      color: textColor,
                    }}
                    data-measure={m}
                    onPointerDown={(event) => handleCellPointerDown(event, m)}
                    title={`Mesure ${m}`}
                  >
                    <span className="text-[11px] font-semibold tabular-nums leading-none">
                      {m}
                    </span>
                    {isFirst && (
                      <div
                        className="absolute left-0 top-0 bottom-0 w-[2px]"
                        style={{ backgroundColor: "var(--color-accent)" }}
                      />
                    )}
                    {isLast && (
                      <div
                        className="absolute right-0 top-0 bottom-0 w-[2px]"
                        style={{ backgroundColor: "var(--color-accent)" }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
